<template>
  <div class="payment-popup">
    <BaseModalWithLogo class="payment-popup__container">
      <div class="payment-popup__form">
        <span class="payment-popup__title">Payment Details</span>
        <span class="payment-popup__subtitle">Enter your payment info to complete your subscription.</span>
        <div class="payment-popup__sections-holder">
          <div class="payment-popup__form-section">
            <span class="payment-popup__form-section-title">Billing details</span>
            <span class="payment-popup__form-section-subtitle">Add your billing details which will be added to your
              invoices</span>
            <div id="address-element"></div>
          </div>

          <div class="payment-popup__form-section">
            <span class="payment-popup__form-section-title">Payment method</span>
            <span class="payment-popup__form-section-subtitle">Add your billing details which will be added to your
              invoices</span>
            <div id="payment-element"></div>
          </div>

        </div>

      </div>
      <div class="payment-popup__info">
        <template v-if="plan">
          <div class="payment-popup__info-header">
            <span class="payment-popup__info-header-title">Selected Plan</span>
            <span class="payment-popup__info-header-plan-name">
              {{ plan.name }}
            </span>
            <Price :price="plan.price" />
          </div>
          <div class="payment-popup__info-pricing">
            <div class="payment-popup__info-pricing-item">
              <span class="payment-popup__info-pricing-item-title">Subtotal</span>
              <span class="payment-popup__info-pricing-item-value">
                ${{ plan.price }}
              </span>
            </div>
            <div v-if="!isPromoCodeFormVisible && !discount"
              class="payment-popup__info-pricing-item">
              <span class="payment-popup__info-pricing-item-title">Promo code</span>
              <a href="#"
                class="payment-popup__info-pricing-item-link"
                @click.stop="showPromoCodeForm">
                Add code
              </a>
            </div>

            <form v-else-if="isPromoCodeFormVisible"
              @submit.prevent="applyPromoCode"
              class="payment-popup__info-pricing-form">
              <span class="payment-popup__info-pricing-form-label">Promo Code</span>
              <div class="payment-popup__info-pricing-form-input-group">
                <input type="text"
                  v-model="promoCode"
                  placeholder="Add promotion code"
                  class="payment-popup__info-pricing-form-input"
                  ref="promoCodeInput"
                  :class="{ 'payment-popup__info-pricing-form-input_invalid': !isPromoCodeFormValid }" />
                <BaseButton 
                  button-style="secondary"
                  class="payment-popup__info-pricing-form-apply-button"
                  @click="applyPromoCode"
                  type="button">
                  Apply
                </BaseButton>
              </div>
              <span class="payment-popup__info-pricing-form-error"
                v-if="!isPromoCodeFormValid">
                This promo code is invalid
              </span>
            </form>
            <template v-else>
              <div class="payment-popup__info-pricing-item">
                <span class="payment-popup__info-pricing-item-title">Discount applied</span>
                <span class="payment-popup__info-pricing-item-value">
                  -${{ calculatedDiscount }}/month
                </span>
              </div>
              <div class="payment-popup__info-pricing-item payment-popup__info-pricing-item">
                <span class="payment-popup__info-pricing-item-title payment-popup__info-pricing-item-title_greyed">
                  Coupon {{ appliedPromoCode }} applied for
                  <div>${{ calculatedDiscount }}/month off.</div>
                </span>
                <span class="payment-popup__info-pricing-item-value">
                  <a href="#"
                    class="payment-popup__info-pricing-item-link"
                    @click.stop="removeDiscount">
                    Remove
                  </a>
                </span>
              </div>
            </template>
            <div class="payment-popup__info-pricing-item">
              <span class="payment-popup__info-pricing-item-title">Due today</span>
              <span class="payment-popup__info-pricing-item-value">
                ${{ finalPrice }}
              </span>
            </div>

            <div v-if="errorMessage" class="payment-popup__error-message">
              <span>{{ errorMessage }}</span>
              <button type="button" class="payment-popup__error-close" @click="clearErrorMessage">
                <BaseMaterialDesignIcon icon="close" />
              </button>
            </div>

            <div class="payment-popup__payment-terms">
              <span class="payment-popup__payment-terms-title">
                Audience Key Terms
              </span>
              <span class="payment-popup__payment-terms-value">
                By clicking Submit Payment, you agree to the Audience Key <a
                  target="_blank" href="https://audiencekey.com/terms-of-service/">terms of service</a>
              </span>
            </div>

            <BaseButton button-style="secondary"
              class="payment-popup__info-pricing-button payment-popup__info-pricing-button_back"
              @click="stepBack">
              <BaseMaterialDesignIcon icon="arrowLeft" />
              Back
            </BaseButton>
            <BaseButton button-style="dark-gray"
              class="payment-popup__info-pricing-button"
              type="button"
              @click="submit">
              Submit Payment
            </BaseButton>
          </div>
        </template>
      </div>
    </BaseModalWithLogo>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import Price from '@/features/agency/common/Price'
export default {
    name: 'PaymentPopup',
    props: {
        plan: {
            type: Object,
            required: true
        }
    },
    components: {
        Price
    },
    data() {
        return {
            paymentPlan: null,
            stripe: null,
            elements: null,
            isFormRendered: false,
            isPromoCodeFormVisible: false,
            isPromoCodeFormValid: true,
            discount: 0,
            discountType: 'fixed', // 'fixed' or 'percentage'
            promoCode: '',
            appliedPromoCode: null,
            isSubmitting: false,
            setupIntentId: null,
            errorMessage: null
        }
    },
    computed: {
      ...mapState(['stripeClientSecret']),
      calculatedDiscount() {
        if (this.discountType === 'percentage') {
          return ((this.plan.price * this.discount) / 100).toFixed(2);
        }
        return this.discount;
      },
      finalPrice() {
        return (this.plan.price - this.calculatedDiscount).toFixed(2);
      }
    },
    watch: {
      stripeClientSecret: {
        handler() {
          // this.renderForm();
        },
      }
    },
    async mounted() {
      // if (this.stripeClientSecret) {
      this.renderForm();
      // }
    },
    methods: {
      ...mapActions('agency', ['checkPromoCode', 'createSubscription']),
      async renderForm() {
        if (this.isFormRendered) return;
        
        try {
          this.errorMessage = null;
          
          this.isFormRendered = true;
          this.stripe = await this.$getStripe();
          
          // Create Elements without client secret - following "collect payment details before creating intent" pattern
          const options = {
            mode: 'subscription',
            amount: this.plan.price * 100, // Convert to cents
            currency: 'usd',
            appearance: {
              theme: 'stripe',
            },
          };

          this.elements = this.stripe.elements(options);
          const addressElementOptions = {
            mode: 'billing',
            autocomplete: {
              mode: 'disabled'
            },
            allowedCountries: ['US'],
          };
          const addressElement = this.elements.create('address', addressElementOptions);
          const paymentElementOptions = { 
            layout: 'tabs',
          };
          const paymentElement = this.elements.create('payment', paymentElementOptions);

          addressElement.mount('#address-element');
          paymentElement.mount('#payment-element');
          this.paymentElement = paymentElement;
        } catch (error) {
          console.error('Error rendering form:', error);
          this.errorMessage = 'Failed to load payment form. Please try again.';
        }
      },
      showPromoCodeForm() {
        this.isPromoCodeFormVisible = true;
        this.promoCode = '';
        // 100 ms delay to ensure the input is rendered
        setTimeout(() => {
          this.$refs.promoCodeInput.focus();
        }, 100);
      },
      async applyPromoCode() {
        const result = await this.checkPromoCode({ promoCode: this.promoCode });
        if (result) {
          this.discount = result.discount;
          this.discountType = result.type;
          this.appliedPromoCode = result.promoCode;
          this.isPromoCodeFormVisible = false;
          this.isPromoCodeFormValid = true;
        } else {
          this.isPromoCodeFormValid = false;
        }
      },
      async submit() {
        if (this.isSubmitting) return;
        
        this.isSubmitting = true;
        this.errorMessage = null;
        
        try {
          // Trigger form validation and wallet collection
          const { error: submitError } = await this.elements.submit();
          if (submitError) {
            console.error('Form validation error:', submitError);
            this.errorMessage = submitError.message || 'Please check your payment details.';
            this.isSubmitting = false;
            return;
          }

          // Create the subscription with the collected payment details
          const subscriptionData = await this.createSubscription({ 
            planId: this.plan.id,
            promoCode: this.appliedPromoCode,
            discountAmount: this.calculatedDiscount
          });
          
          // Confirm the Intent using the details collected by the Payment Element
          let confirmIntent;
          if (subscriptionData.type === 'setup') {
            confirmIntent = this.stripe.confirmSetup;
          } else {
            confirmIntent = this.stripe.confirmPayment;
          }
          const { error } = await confirmIntent({
            elements: this.elements,
            clientSecret: subscriptionData.clientSecret,
            confirmParams: {
              return_url: window.location.origin + '/dashboard#/strategy/keyword-census',
            },
            redirect: 'if_required'
          });

          if (error) {
            console.error('Payment confirmation error:', error);
            // Extract the error message from the Stripe error object
            this.errorMessage = error.message || 'Payment failed. Please try again.';
            this.isSubmitting = false;
            return;
          }

          // Payment is being processed, show success step
          this.$emit('submit', { success: true });
        } catch (error) {
          console.error('Error submitting payment:', error);
          this.errorMessage = 'An error occurred while processing your payment. Please try again.';
        } finally {
          this.isSubmitting = false;
        }
      },
      stepBack() {
        this.isPromoCodeFormVisible = false;
        this.$emit('back');
      },
      removeDiscount() {
        this.discount = 0;
        this.discountType = 'fixed';
        this.appliedPromoCode = null;
      },
      clearErrorMessage() {
        this.errorMessage = null;
      }
    }
}
</script>

<style lang="scss" scoped>
.payment-popup {
  display: flex;
  flex-direction: column;
  height: 100%;
  max-height: 90vh;
  min-height: 510px;
  
  &__container {
    flex: 1 1 auto;
    display: flex;
    overflow: hidden;
    position: relative;
  }

  &__form {
    flex: 0 0 560px;
    padding: 48px 40px 32px 48px;
    box-sizing: border-box;
    overflow: hidden;
    display: flex;
    flex-direction: column;
  }

  &__info {
    flex: 1;
    background-color: $rfx-light-turquoise;
  }

  &__title {
    display: block;
    font-size: 24px;
    font-weight: 700;
    color: $rfx-dark-march;
    margin-bottom: 12px;
  }

  &__subtitle {
    display: block;
    font-size: 16px;
    color: $rfx-text-dark-mid-contrast;
    padding-bottom: 24px;
    position: relative;

    &::before {
      content: '';
      display: block;
      width: 100%;
      height: 1px;
      background-color: $rfx-medium-steel;
      position: absolute;
      bottom: 0;
      left: 0;
    }
  }


  &__sections-holder {
    flex: 1;
    overflow-y: auto;
    margin-right: -25px;
    padding-right: 25px;
    padding-top: 24px;
  }

  &__form-section {

    &:not(:first-child) {
      margin-top: 44px;
      border-top: 1px solid $rfx-medium-steel;
      padding-top: 24px;
    }
  }

  &__form-section-title {
    display: block;
    font-size: 20px;
    font-weight: 700;
    color: $rfx-dark-march;
    margin-bottom: 12px;
  }

  &__form-section-subtitle {
    display: block;
    font-size: 14px;
    color: $rfx-text-dark-mid-contrast;
    margin-bottom: 36px;
  }

  &__error-message {
    display: flex;
    align-items: center;
    justify-content: space-between;
    color: $rfx-red;
    font-size: 14px;
    margin-top: 12px;
    padding: 8px 12px;
    background-color: rgba(255, 0, 0, 0.1);
    border: 1px solid $rfx-red;
    border-radius: 4px;
  }

  &__error-close {
    background: none;
    border: none;
    color: $rfx-red;
    cursor: pointer;
    padding: 4px;
    margin-left: 8px;
    display: flex;
    align-items: center;
    justify-content: center;

    &:hover {
      background-color: rgba(255, 0, 0, 0.2);
      border-radius: 4px;
    }

    .material-design-icon {
      font-size: 16px;
    }
  }

  &__info {
    padding: 88px 32px 104px;
    position: relative;
    display: flex;
    flex-direction: column;
  }

  &__info-icon {
    position: absolute;
    top: 28px;
    right: 28px;
    width: 40px;
    height: 40px;
  }

  &__info-header {
    padding: 0 16px 20px;
    display: flex;
    flex-direction: column;
    border-bottom: 1px solid $rfx-medium-steel;
    margin-bottom: 20px;
  }

  &__info-header-title {
    display: block;
    font-size: 14px;
    color: $rfx-text-dark-mid-contrast;
    margin-bottom: 20px;
    margin-left: -16px;
  }

  &__info-header-plan-name {
    display: block;
    font-size: 24px;
    font-weight: 600;
    color: $rfx-dark-march;
    margin-bottom: 8px;
  }

  &__info-pricing {
    display: flex;
    flex-direction: column;
    overflow: hidden;
    flex: 1;
    gap: 12px;
    color: $rfx-dark-march;
    font-size: 14px;
    padding: 0 16px;
  }

  &__info-pricing-item {
    display: flex;
    justify-content: space-between;
    gap: 8px;
  }

  &__info-pricing-item-value {
    white-space: nowrap;
  }

  &__info-pricing-item-title_greyed {
    color: $rfx-text-dark-mid-contrast;
  }

  &__info-pricing-item-link {
    color: $rfx-text-dark-mid-contrast;
    text-decoration: underline;

    &:hover {
      text-decoration: none;
    }
  }

  &__info-pricing-form {
    display: flex;
    gap: 4px;
    flex-direction: column;
    padding-bottom: 8px;
  }

  &__info-pricing-form-input-group {
    display: flex;
    gap: 8px;
    align-items: center;
  }

  &__info-pricing-form-label {
    display: block;
    font-weight: 700;
  }

  &__info-pricing-form-input {
    display: block;
    padding: 0 12px;
    height: 40px;
    border: 1px solid $rfx-medium-steel;
    border-radius: 4px;
    font-size: 14px;
    color: $rfx-dark-march;
    box-shadow: none !important;
    outline: none !important;

    &_invalid {
      border-color: $rfx-red;
    }

    &::placeholder {
      color: $rfx-text-dark-low-contrast;
    }
  }

  &__info-pricing-form-error {
    display: block;
    color: $rfx-red;
    font-size: 11px;
    margin-top: 4px;
  }

  &__info-pricing-form-apply-button {
    height: 40px;
    padding: 0 16px;
    font-size: 14px;
    white-space: nowrap;

    &:hover {
      background-color: $rfx-medium-steel !important;
      color: $rfx-dark-march !important;
    }
  }

  &__payment-terms {
    position: absolute;
    padding: 20px 16px;
    border-top: 1px solid $rfx-medium-steel;
    left: 32px;
    bottom: 72px;
    width: calc(100% - 64px);
    box-sizing: border-box;
  }

  &__payment-terms-title {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 14px;
    color: $rfx-text-dark-mid-contrast;
    font-weight: 600;
    margin-bottom: 8px;

    .material-design-icon {
      font-size: 18px;
      margin-top: -3px;
    }
  }

  &__payment-terms-value {
    color: $rfx-text-dark-low-contrast;

    a {
      color: $rfx-text-dark-low-contrast;
      text-decoration: underline;

      &:hover {
        text-decoration: none;
      }

    }
  }

  &__info-pricing-button {
    position: absolute;
    bottom: 32px;
    right: 32px;
    display: flex;
    align-items: center;
    gap: 4px;
    color: white;
    padding-right: 16px !important;

    &_back {
      right: auto;
      left: 32px;
      color: $rfx-dark-march !important;
    }

    .material-design-icon {
      color: inherit;
    }
  }

}
</style>